// https://hexdocs.pm/phoenix_live_view/js-interop.html#client-hooks
// Note: when using phx-hook, a unique DOM ID must always be set.

import { Chart } from "chart.js/auto";
import "chartjs-adapter-date-fns";
import autocolors from "chartjs-plugin-autocolors";

Chart.register(autocolors);

const ChartJsHook = {
  mounted() {
    this._initChart();
  },
  updated() {
    // LiveView patched the element; rebuild the chart with new options
    if (this.chart) {
      try {
        this.chart.destroy();
      } catch (_) {}
      this.chart = null;
    }
    this._initChart();
  },
  destroyed() {
    if (this._boundHandler && this._boundEventName) {
      window.removeEventListener("phx:" + this._boundEventName, this._boundHandler);
      this._boundHandler = null;
      this._boundEventName = null;
    }
    if (this.chart) {
      try {
        this.chart.destroy();
      } catch (_) {}
      this.chart = null;
    }
  },
  _initChart() {
    // Defer actual initialization to ensure DOM children (canvas) are present
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => this._reallyInitChart());
  },
  _reallyInitChart() {
    const rawOptions = this.el.dataset.chartOptions || this.el.dataset.chartData;
    if (!rawOptions) return;
    const chartOptions = JSON.parse(rawOptions);
    const autocolorEnabled = this.el.dataset.autocolor === "true";
    const yLabelFormat = this.el.dataset.yLabelFormat;

    chartOptions.options ||= {};
    chartOptions.options.plugins ||= {};
    chartOptions.options.plugins.autocolors ||= {};
    chartOptions.options.plugins.autocolors.enabled = autocolorEnabled;

    chartOptions.options.plugins.legend ||= {};
    if (typeof chartOptions.options.plugins.legend.display === "undefined") {
      chartOptions.options.plugins.legend.display = true;
    }

    chartOptions.options.scales ||= {};
    chartOptions.options.scales.x ||= {};
    chartOptions.options.scales.x.ticks ||= {};
    chartOptions.options.scales.x.grid ||= {};
    chartOptions.options.scales.y ||= {};
    chartOptions.options.scales.y.ticks ||= {};
    chartOptions.options.scales.y.grid ||= {};

    // High-contrast defaults
    const isDark = document.documentElement.classList.contains("dark");
    const tickColor = isDark ? "#E5E7EB" : "#374151"; // gray-200 vs gray-700
    const gridColor = isDark ? "#334155" : "#E5E7EB"; // slate-700 vs gray-200
    chartOptions.options.scales.x.ticks.color ||= tickColor;
    chartOptions.options.scales.y.ticks.color ||= tickColor;
    chartOptions.options.scales.x.grid.color ||= gridColor;
    chartOptions.options.scales.y.grid.color ||= gridColor;

    chartOptions.options.scales.y.ticks.callback = (value, _index, _ticks) =>
      formatValue(value, yLabelFormat);

    // Create the chart
    const canvas = this.el.querySelector("canvas");
    if (!canvas) return;
    const context = canvas.getContext("2d");

    const createGradient = (ctx, colors) => {
      const gradient = ctx.createLinearGradient(0, 0, 0, 300);
      gradient.addColorStop(0, colors.from);
      gradient.addColorStop(1, colors.to);
      return gradient;
    };

    // Apply gradients to datasets if specified
    if (chartOptions.data && chartOptions.data.datasets) {
      chartOptions.data.datasets = chartOptions.data.datasets.map((dataset) => {
        if (dataset.useGradient && chartOptions.type === "line") {
          return {
            ...dataset,
            backgroundColor: createGradient(context, {
              from: dataset.gradientFrom,
              to: dataset.gradientTo,
            }),
          };
        }
        return dataset;
      });
    }

    this.chart = new Chart(context, chartOptions);

    const event = this.el.dataset.event;
    if (event) {
      // Remove any prior listener for this element by using a bound handler stored on the hook
      if (this._boundHandler && this._boundEventName) {
        window.removeEventListener("phx:" + this._boundEventName, this._boundHandler);
      }
      this._boundEventName = event;
      this._boundHandler = (e) => {
        const updatedDatasets = e.detail.datasets.map((dataset) => {
          if (dataset.useGradient && chartOptions.type === "line") {
            return {
              ...dataset,
              backgroundColor: createGradient(context, {
                from: dataset.gradientFrom,
                to: dataset.gradientTo,
              }),
            };
          }
          return dataset;
        });
        this.chart.data.datasets = updatedDatasets;
        this.chart.data.labels = e.detail.labels;
        this.chart.update();
      };
      window.addEventListener("phx:" + this._boundEventName, this._boundHandler);
    }
  },
};

function formatValue(value, format) {
  switch (format) {
    case "dollars":
      return formatDollars(value);
      break;
    case "percent":
      return numberWithCommas(value) + "%";
      break;
    default:
      return numberWithCommas(value);
  }
}

function formatDollars(value) {
  return Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumSignificantDigits: 3,
    notation: "compact",
  }).format(value);
}

function numberWithCommas(value) {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export default ChartJsHook;
